/**
 * A website source's label: the page the user wants read, as they see it.
 *
 * Any page can be a source, not only a site's root (CON-8612), so the label
 * keeps the path and query. Lives apart from the intake hook so the failure
 * copy can name a source by its host without importing the hook.
 */
import { isLikelyDomain, normalizeDomain } from './hooks/use-brand-prefill-domain';

/**
 * A website as the user typed it, as a source label: the page URL without its
 * scheme, with the host lowercased and its trailing dots dropped (the path and
 * query keep their case, which a server may care about). A `#anchor` only
 * scrolls the page, so it is dropped; a `#/route` or `#!route` picks what a
 * single-page app renders, so it is kept. A bare root path is dropped and a
 * query or route with no path gets one, so a page has one label (which is what
 * dedupe compares) and the host is everything before the first `/`.
 */
export function normalizeSourceUrl(input: string): string {
  const url = input
    .trim()
    .replace(/^https?:\/\//iu, '')
    .replace(/#(?![/!]).*$/u, '');
  const hostEnd = url.search(/[/?#]/u);
  const host = normalizeDomain(hostEnd === -1 ? url : url.slice(0, hostEnd));
  const rest = hostEnd === -1 ? '' : url.slice(hostEnd);
  if (rest === '/') return host;
  return rest.startsWith('/') || rest === '' ? `${host}${rest}` : `${host}/${rest}`;
}

/** The host of a normalized source label: everything before its first `/`. */
export function sourceUrlHost(label: string): string {
  return label.split('/', 1)[0] ?? label;
}

/**
 * Whether a normalized source label is a page we can submit: a likely domain,
 * and nothing the backend's URL parser refuses: whitespace or control
 * characters, or a `%` in the path or a kept `#/route` that doesn't start an
 * escape, as in "sale-50%". (Go leaves the query's escapes alone, so
 * "?q=100%" is fine, but not a route's, which is part of the fragment.) Caught
 * here, those get "Enter a valid URL." rather than a refusal that calls a
 * plausible address invalid.
 */
export function isLikelySourceUrl(label: string): boolean {
  // Only a "?" before any "#" starts the query.
  const withoutQuery = label.replace(/^([^#?]*)\?[^#]*/u, '$1');
  return isLikelyDomain(sourceUrlHost(label)) && !/[\s\p{Cc}]/u.test(label) && !/%(?![\da-f]{2})/iu.test(withoutQuery);
}
